import { useHistory, type match } from 'react-router-dom'
import { Alert, PageHeader, message } from 'antd'
import { Box } from '@weareberyl/design-system'
import type { DeepPartial } from '@apollo/client/utilities'

import ProductForm from '../ProductForm'
import {
  SchemeProductListDocument,
  useCreateSchemeProductMutation,
  type SchemeProduct,
  type SchemeProductInput,
} from '~/gql/generated/graphql'
import { prependScheme } from '~/constants/routes'

type Props = {
  match: match<{ scheme_id: string; id: string }>
}

const ProductCreate = ({ match }: Props) => {
  const history = useHistory()
  const [createSchemeProduct, { loading: saving, error }] =
    useCreateSchemeProductMutation()

  // Display messages from the server if there are errors that get past client side form validation
  const messages = error?.graphQLErrors.map(error => error.message)

  const onFinish = async (values: Omit<SchemeProductInput, 'uuid'>) => {
    const schemeProductInput = {
      uuid: crypto.randomUUID().toString(),
      ...values,
    }

    try {
      const { data } = await createSchemeProduct({
        variables: { schemeProductInput },
        refetchQueries: [SchemeProductListDocument],
      })

      history.replace(
        prependScheme(
          `/products/${data?.add_scheme_product.id}`,
          match.params.scheme_id,
        ),
      )
    } catch {
      message.error('There was an error saving the product')
      return
    }

    message.success('Product created')
  }

  const initialValues: DeepPartial<SchemeProduct> = {
    scheme_id: match.params.scheme_id,
    amount: 0, // Default free but not null if field is hidden for payg
    configs_v3: {
      bike: {},
    },
    details: {
      bullets: [],
    },
  }

  return (
    <>
      <PageHeader title="Create new product" onBack={history.goBack} />
      {messages && (
        <Box pb={3}>
          <Alert
            message="Couldn't create the product"
            description={messages.map(message => (
              <p key={message}>{message}</p>
            ))}
            type="error"
            showIcon
          />
        </Box>
      )}
      <ProductForm
        initialValues={initialValues}
        loading={saving}
        onFinish={onFinish}
      />
    </>
  )
}

export default ProductCreate
